import * as Sentry from "@sentry/react";
import {
  SheetDescription,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import {
  downloadPersonaSelection,
  personaSelectionFilename,
  type PersonaDocumentExportFormat,
  type PersonaDocxPart,
} from "@/features/persona-opportunities/lib/persona-script-docx";

import { DocumentExportMenu } from "../../shared/DocumentExportMenu/DocumentExportMenu.js";
import { FavoriteToggleButton } from "../../shared/FavoriteToggleButton/FavoriteToggleButton.js";
import {
  PersonaScriptAiEditButton,
  PersonaScriptCopyButton,
  PersonaScriptShareButton,
  PersonaScriptVendorAction,
} from "../../workspace/tabs/ScriptsTab/PersonaScriptActionsFooter.js";

/** Matches the 36px copy/share squares so the header controls share one size. */
const HEADER_ICON_BUTTON_CLASS =
  "h-9 w-9 min-h-9 min-w-9 p-0 has-[>svg]:p-0 [&_svg]:size-4";

export interface OpportunityHubDrawerToolbarProps {
  sheetTitle: string;
  setName: string;
  isFavorite: boolean;
  /** Omit when the viewer cannot change favorites; the star stays visible but disabled. */
  onToggleFavorite?: () => void;
  copied: boolean;
  onCopy: () => void;
  /**
   * Current script or brief. The header offers Text and Word, including custom
   * assets and every set type. Omit on a read-only older version.
   */
  exportDocument?: OpportunityHubDrawerExportDocument;
  copyLabel?: string;
  linkCopied: boolean;
  isSharing?: boolean;
  onShare: () => void;
  /** Document-specific actions placed before AI Edit, e.g. the script's "Add 3 hooks". */
  extraActions?: ReactNode;
  isAiEditing: boolean;
  /** Omit when the viewer cannot AI edit. */
  onAiEdit?: () => void;
  sentMeta: SentScriptMeta | null;
  onSendToVendor: () => void;
  orderHref?: string;
  onOpenOrder?: (
    orderId: string,
    orderScriptId: string | undefined,
    event: ReactMouseEvent<HTMLAnchorElement>,
  ) => void;
  /** Older versions are read-only previews, so every button acts on the wrong content. */
  hideActions?: boolean;
}

export interface OpportunityHubDrawerExportDocument {
  part: PersonaDocxPart;
}

/** Sheet header row for the hub script and brief drawers. */
export function OpportunityHubDrawerToolbar({
  sheetTitle,
  setName,
  isFavorite,
  onToggleFavorite,
  copied,
  onCopy,
  exportDocument,
  copyLabel,
  linkCopied,
  isSharing = false,
  onShare,
  extraActions,
  isAiEditing,
  onAiEdit,
  sentMeta,
  onSendToVendor,
  orderHref,
  onOpenOrder,
  hideActions = false,
}: OpportunityHubDrawerToolbarProps) {
  const { t } = useTranslation();
  const copyTooltip = copied
    ? t("personaOpportunities.scripts.actions.copied", "Copied!")
    : (copyLabel ?? t("personaOpportunities.scripts.actions.copy", "Copy Script"));
  const shareTooltip = linkCopied
    ? t("personaOpportunities.scripts.actions.shareCopied", "Link copied!")
    : t("personaOpportunities.scripts.actions.share", "Share");
  const favoriteTooltip = isFavorite
    ? t("personaOpportunities.scripts.unfavoriteAria", "Remove from favorites")
    : t("personaOpportunities.scripts.favoriteAria", "Add to favorites");

  return (
    <div className="flex min-h-16 items-center gap-2 pl-14">
      <SheetTitle className="sr-only">{sheetTitle}</SheetTitle>
      <SheetDescription className="sr-only">{setName}</SheetDescription>
      {hideActions ? null : (
        <>
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2">
              <DrawerActionTooltip label={favoriteTooltip}>
                <FavoriteToggleButton
                  isFavorite={isFavorite}
                  onToggle={onToggleFavorite}
                  variant="secondary"
                  size="default"
                  className={HEADER_ICON_BUTTON_CLASS}
                />
              </DrawerActionTooltip>
              <DrawerActionTooltip label={copyTooltip}>
                <PersonaScriptCopyButton copied={copied} onCopy={onCopy} label={copyLabel} />
              </DrawerActionTooltip>
              {exportDocument ? <DrawerExportMenu document={exportDocument} /> : null}
              <DrawerActionTooltip label={shareTooltip}>
                <PersonaScriptShareButton
                  linkCopied={linkCopied}
                  isSharing={isSharing}
                  onShare={onShare}
                />
              </DrawerActionTooltip>
            </div>
          </TooltipProvider>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {extraActions}
            {onAiEdit ? (
              <PersonaScriptAiEditButton tone="neutral" isAiEditing={isAiEditing} onAiEdit={onAiEdit} />
            ) : null}
            <PersonaScriptVendorAction
              sentMeta={sentMeta}
              onSendToVendor={onSendToVendor}
              orderHref={orderHref}
              onOpenOrder={onOpenOrder}
              sentPresentation="button"
            />
          </div>
        </>
      )}
    </div>
  );
}

function DrawerExportMenu({ document }: { document: OpportunityHubDrawerExportDocument }) {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  async function handleExport(format: PersonaDocumentExportFormat) {
    setIsExporting(true);
    try {
      await downloadPersonaSelection(
        [document.part],
        format,
        personaSelectionFilename(
          document.part.kind === "script" ? 1 : 0,
          document.part.kind === "brief" ? 1 : 0,
          format,
        ),
      );
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: "persona-hub-drawer-export" } });
      toast.error(
        t("personaOpportunities.scripts.actions.exportFailed", "Could not export this document"),
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <DocumentExportMenu
      busy={isExporting}
      iconOnly
      variant="secondary"
      size="default"
      className={HEADER_ICON_BUTTON_CLASS}
      testId="opportunity-hub-drawer-export"
      onExport={(format) => void handleExport(format)}
    />
  );
}

function DrawerActionTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
